import { MediaItem, MediaSaveRespose } from '../../types';
import { captureVideoSnapshot } from './mediaItemService';
import { calculateInsertionPosition, createMediaItem, sortMediaItemsByStartTime } from '../../utils/mediaItemUtils';
import { createBlobURL } from '../../utils/blobURLManager'; // ✅ Import blob URL manager

export interface UploadHandlerParams {
  addAtCurrentTime: boolean;
  currentTime: number;
  detectAspectRatio: (items: MediaItem[]) => void;
  showNotification: (notification: any) => void;
}

/**
 * Handle media upload from file input or API response
 * ✅ UPDATED: Now uses tracked blob URLs for proper cleanup
 */
export const handleMediaUpload = (
  eventOrFileUrl: React.ChangeEvent<HTMLInputElement> | MediaSaveRespose[],
  type: string,
  params: UploadHandlerParams,
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>
) => {
  let data: MediaSaveRespose[];

  if (type === "event") {
    const files = (eventOrFileUrl as React.ChangeEvent<HTMLInputElement>).target.files;
    if (!files || files.length === 0) return;

    // ✅ Use tracked blob URL creation instead of direct URL.createObjectURL
    data = Array.from(files).map((file) => ({
      url: createBlobURL(file), // ✅ Now tracked for cleanup
      fileType: file.type,
      name: file.name
    }));
  } else if (Array.isArray(eventOrFileUrl)) {
    if (!eventOrFileUrl || eventOrFileUrl.length === 0) return;
    data = eventOrFileUrl;
  } else return;

  const { addAtCurrentTime, currentTime, detectAspectRatio, showNotification } = params;

  data.forEach((mediaItem) => {
    if (mediaItem.fileType.startsWith('video')) {
      handleVideoUpload(mediaItem, addAtCurrentTime, currentTime, setMediaItems, detectAspectRatio, showNotification);
    } else if (mediaItem.fileType.startsWith('image')) {
      handleImageUpload(mediaItem, addAtCurrentTime, currentTime, setMediaItems);
    }
  });

  // Detect aspect ratio after uploads
  if (data && data.length > 0) {
    setTimeout(() => {
      setMediaItems(currentItems => {
        if (currentItems.length > 0) {
          detectAspectRatio(currentItems);
        }
        return currentItems;
      });
    }, 500);
  }
};

/**
 * Handle video file upload
 */
const handleVideoUpload = (
  mediaItem: MediaSaveRespose,
  shouldAddAtCurrentTime: boolean,
  uploadCurrentTime: number,
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>,
  detectAspectRatio: (items: MediaItem[]) => void,
  showNotification: (notification: any) => void
) => {
  captureVideoSnapshot(mediaItem.url)
    .then(({ snapshotUrl, duration }) => {
      setMediaItems(prevItems => {
        const { start, end } = calculateInsertionPosition(
          prevItems,
          shouldAddAtCurrentTime,
          uploadCurrentTime,
          duration
        );

        const tempMedia = createMediaItem(
          mediaItem.name,
          mediaItem.url,
          'video',
          start,
          end,
          {
            src: snapshotUrl,
            idPrefix: 'uploaded-video'
          }
        );

        return sortMediaItemsByStartTime([...prevItems, tempMedia]);
      });

      setTimeout(() => {
        setMediaItems(currentItems => {
          if (currentItems.length > 0) {
            detectAspectRatio(currentItems);
          }
          return currentItems;
        });
      }, 300);

      // showNotification({ type: 'success', message: 'Snapshot captured successfully!', title: 'Success' });
    })
    .catch((error) => {
      showNotification({ type: 'error', message: `Error capturing snapshot! ${error.message}`, title: 'Error' });
    });
};

/**
 * Handle image file upload
 */
const handleImageUpload = (
  mediaItem: MediaSaveRespose,
  shouldAddAtCurrentTime: boolean,
  uploadCurrentTime: number,
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>
) => {
  setMediaItems(prevItems => {
    const imageDuration = 5;
    const { start, end } = calculateInsertionPosition(
      prevItems,
      shouldAddAtCurrentTime,
      uploadCurrentTime,
      imageDuration
    );

    const tempMedia = createMediaItem(
      mediaItem.name,
      mediaItem.url,
      'image',
      start,
      end,
      {
        idPrefix: 'uploaded-image'
      }
    );

    return sortMediaItemsByStartTime([...prevItems, tempMedia]);
  });
};